// Hideo - Saved videos page (src/pages/SavedPage.vue): videos found on the socials (reels, shorts, TikToks…) that the
// user wants to find again later, in data.savedVideos. Like Sources, one big button per group: the groups are the
// video's tags (the 'saved' group of data.projectTags, separate from the plan's formats, so that the two never mix;
// user request 2/11/2026: before, the buttons were the platforms). Tapping a button opens the compact list of its
// videos, newest first, with search. Above the buttons, the platform row (recognised from the link, PUB_SOCIALS of
// src/features/published.ts) and the category row filter everything. The dialog is SavedVideoDialog.vue.
import { t } from '../i18n'
import type { ProfileData, PubLinkKey, SavedVideo } from '../data/types'
import { sameTag } from '../data/tags'
import { normUrl, ytThumb } from '../utils/url'
import { PUB_SOCIALS, pubDetect } from './published'
import { TAG_NONE, barTagsOf, ofTag, tagList } from './tags'
export { TAG_NONE }
import { ofProject, validProject } from './projects'
import { firstLine, removeItem, textMatches } from '../data/items'
import { KIND_NONE, allKinds, ofKind } from './categories'

/** Open tile: '' = none, SAVED_ALL = every video, TAG_NONE = videos without tags, otherwise a tag name */
export const SAVED_ALL = '*'

/** The video's link ('' without one) */
export const savedUrl = (v: Pick<SavedVideo, 'url'>): string => (v.url ? normUrl(v.url) : '')
/** Platform of the video, from its link ('altro' for unknown sites and for videos without a link) */
const platformOf = new WeakMap<object, [url: string, key: PubLinkKey]>()
/** (Recognising the platform parses the link: the result is kept per video while its link stays the same, since the page
 *  asks it for every video once per platform button) */
export function savedPlatform(v: Pick<SavedVideo, 'url'>): PubLinkKey {
  const c = platformOf.get(v)
  if (c && c[0] === v.url) return c[1]
  const key = pubDetect(savedUrl(v)) || 'altro'
  platformOf.set(v, [v.url, key])
  return key
}
/** [key, name, icon, colour] of a platform */
export const platformInfo = (k: string) => {
  const s = PUB_SOCIALS.find(x => x[0] === k) || PUB_SOCIALS[PUB_SOCIALS.length - 1]
  return { key: s[0], name: s[1], icon: s[2], color: s[3] }
}
/** Image of a video in the list: its own thumbnail, otherwise the YouTube one ('' = the platform icon is shown) */
export const savedThumb = (v: Pick<SavedVideo, 'url' | 'thumbnail'>): string => v.thumbnail || ytThumb(savedUrl(v))
/** Name shown for a video: its title, otherwise "<platform> video" */
export const savedTitle = (v: Pick<SavedVideo, 'title' | 'url'>): string => v.title || t('{platform} video', { platform: platformInfo(savedPlatform(v)).name })

/** Saved videos of the active project ('all' = every video) */
const projectSaved = (d: ProfileData, active: string): SavedVideo[] =>
  ofProject(d.savedVideos, active)

// ===== Tag filter ('' = all, TAG_NONE = without tags, otherwise a tag name, compared without case) =====
/** Videos of a tile */
const savedOfTab = (list: SavedVideo[], tab: string): SavedVideo[] => (tab === SAVED_ALL ? list : ofTag(list, tab))

export interface SavedTile {
  /** SAVED_ALL, TAG_NONE or the tag (with the spelling of the bar) */
  key: string
  kind: 'all' | 'tag' | 'none'
  count: number
}
/** A pill of the platform row: '' = "All", otherwise a platform key */
interface SavedPlatformChip {
  key: string
  name: string
  icon: string
  /** Brand colour ('' = text colour) */
  color: string
  count: number
}

/** A pill of the category row: '' = "All", KIND_NONE = "No category", otherwise a category id */
interface SavedKindChip {
  key: string
  name: string
  icon: string
  count: number
}
/** The page: the platform row ("All" and one pill per platform that has videos, in the order of PUB_SOCIALS, "Other
 *  link" last; only with two platforms or more), then the category row (after the platform filter: "All", the
 *  categories used, "No category"; empty when no video has a category), then the big buttons ("All videos", one per tag
 *  of the shown projects that has videos, "No tag" when some videos have tags and some do not), everything after both
 *  filters. platform and kind come back checked ('' when no longer offered) */
export function savedView(d: ProfileData, active: string, platform = '', kind = ''): { videos: SavedVideo[]; tiles: SavedTile[];
  platforms: SavedPlatformChip[]; platform: string; kinds: SavedKindChip[]; kind: string } {
  const inProject = projectSaved(d, active), tags = barTagsOf(d, 'saved', active)
  const used: SavedPlatformChip[] = []
  for (const [key, name, icon, color] of PUB_SOCIALS) {
    const count = inProject.filter(v => savedPlatform(v) === key).length
    if (count) used.push({ key, name, icon, color, count })
  }
  const platforms = used.length > 1 ? [{ key: '', name: '', icon: '', color: '', count: inProject.length }, ...used] : []
  if (!platforms.some(p => p.key === platform)) platform = ''
  const ofPlatform = platform ? inProject.filter(v => savedPlatform(v) === platform) : inProject
  const usedKinds = allKinds(d, 'saved').map(k => ({ key: k.id, name: k.name, icon: k.icon, count: ofKind(ofPlatform, k.id).length })).filter(k => k.count)
  const noKind = ofKind(ofPlatform, KIND_NONE).length
  const kinds = usedKinds.length ? [{ key: '', name: '', icon: '', count: ofPlatform.length }, ...usedKinds, ...(noKind ? [{ key: KIND_NONE, name: '', icon: '', count: noKind }] : [])] : []
  if (!kinds.some(k => k.key === kind)) kind = ''
  const videos = ofKind(ofPlatform, kind)
  const tagTiles: SavedTile[] = tags.map(tag => ({ key: tag, kind: 'tag' as const, count: ofTag(videos, tag).length })).filter(x => x.count)
  const untagged = ofTag(videos, TAG_NONE).length
  const tiles: SavedTile[] = [{ key: SAVED_ALL, kind: 'all', count: videos.length }, ...tagTiles,
    ...(untagged && tagTiles.length ? [{ key: TAG_NONE, kind: 'none' as const, count: untagged }] : [])]
  return { videos, tiles, platforms, platform, kinds, kind }
}

/** Search on title, link and note (mentions as plain text, through `plain`); q already lower case */
export const matchesSaved = (v: SavedVideo, q: string, plain: (text: string) => string): boolean =>
  textMatches(q, savedTitle(v), savedUrl(v), plain(v.note || ''))

/** List of the open tile, newest first; `all` = before the search (to tell "no results" from "no videos") */
export function savedList(list: SavedVideo[], tab: string, query: string, plain: (text: string) => string): { all: SavedVideo[]; list: SavedVideo[] } {
  const all = savedOfTab(list, tab).slice().sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0))
  const q = query.trim().toLowerCase()
  return { all, list: q ? all.filter(v => matchesSaved(v, q, plain)) : all }
}

/** The open tile, still valid (with the spelling of the bar): a tag without videos left (after the filters) closes it */
export const validSavedTab = (view: { tiles: SavedTile[] }, tab: string): string =>
  (!tab ? '' : (view.tiles.find(x => x.key === tab || (x.kind === 'tag' && sameTag(x.key, tab))) || { key: '' }).key)

// ===== Saved video dialog (SavedVideoDialog.vue) =====
interface SavedForm {
  /** As typed: "https://" is added on save */
  url: string
  title: string
  projectId: string
  /** '' | "media:<id>" | an image address (ImageBox.vue) */
  thumbnail: string
  /** With @mentions as "@[type:id]" (src/features/mentions.ts) */
  note: string
  /** Saved videos tags (tags the project lacks are added to it by normalizeData, like hooks moved to another project) */
  tags: string[]
  /** Category ('' = none; src/features/categories.ts) */
  kind: string
}

/** The dialog's starting fields: those of the video, or empty for a new one (project of new items: newProject; tag of
 *  the page's tag filter, if the project has it: tag; the page's category filter: kind) */
export function savedForm(d: ProfileData, id: string | null, newProject: string, tag = '', kind = ''): SavedForm {
  const v = id ? d.savedVideos.find(x => x.id === id) : null
  if (v) return { url: v.url || '', title: v.title || '', projectId: validProject(d, v.projectId, newProject), thumbnail: v.thumbnail || '', note: v.note || '', tags: [...v.tags], kind: v.kind || '' }
  const own = tag && tag !== TAG_NONE ? tagList(d, 'saved', newProject).find(x => sameTag(x, tag)) : ''
  return { url: '', title: '', projectId: newProject, thumbnail: '', note: '', tags: own ? [own] : [], kind }
}

/** A link dropped on the Saved videos page (src/stores/drop.ts): the first line, when it is a web address */
export function droppedSaved(text: string): { url: string } | null {
  const first = firstLine(text)
  return /^https?:\/\//i.test(first) ? { url: first } : null
}

/** Saves the dialog into the data: a new video (id null) or the video with that id (its save date kept). null when the
 *  link is empty (nothing changes). The caller saves. */
export function saveSaved(d: ProfileData, id: string | null, f: SavedForm, now = Date.now()): { video: SavedVideo; created: boolean } | null {
  const url = normUrl(f.url)
  if (!url) return null
  const old = id ? d.savedVideos.find(x => x.id === id) : undefined
  const video: SavedVideo = { ...(old || {}), id: old ? old.id : 'sv_' + now, title: f.title.trim(), url, projectId: f.projectId,
    thumbnail: f.thumbnail, note: f.note.trim(), tags: [...f.tags], kind: f.kind, savedAt: old ? old.savedAt : now }
  if (old) {
    d.savedVideos[d.savedVideos.indexOf(old)] = video
    return { video, created: false }
  }
  d.savedVideos.push(video)
  return { video, created: true }
}

/** Removes a saved video from the data (true if it was there). The caller saves. */
export function deleteSaved(d: ProfileData, id: string): boolean {
  return removeItem(d, 'savedVideos', id)
}
